import { Button, Empty, Flex, Popconfirm, Tag } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Delete,
  DragMenu,
  EditWithoutLine,
  PlusBlueWithoutBackground,
} from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import {
  CONTACT_FORM_CHOICE_FIELD_TYPES,
  CONTACT_FORM_ALIAS_PATTERN,
  MAX_CONTACT_FORM_FIELDS,
} from '../../lib/constants';
import type { ContactFormField, ContactFormFieldType, PresetFormValues } from '../../lib/types';
import { ContactFormFieldDraft, type FieldDraftErrors } from './contact-form-field-draft';
import styles from './preset-form.module.scss';

interface ContactFormFieldsEditorProps {
  control: Control<PresetFormValues>;
  disabled?: boolean;
}

type SortableFieldRenderProps = Pick<
  ReturnType<typeof useSortable>,
  'attributes' | 'isDragging' | 'listeners' | 'setActivatorNodeRef'
>;

interface SortableFieldProps {
  id: string;
  disabled: boolean;
  children: (props: SortableFieldRenderProps) => ReactNode;
}

const SortableField = ({ id, disabled, children }: SortableFieldProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled });
  const style: CSSProperties = {
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition: isDragging ? undefined : transition,
  };

  return (
    <div ref={setNodeRef} className={styles.sortableField} style={style}>
      {children({ attributes, isDragging, listeners, setActivatorNodeRef })}
    </div>
  );
};

const createEmptyField = (): ContactFormField => ({
  alias: '',
  type: 'text',
  label: '',
  placeholder: null,
  required: false,
  options: [],
});

const isChoiceField = (type: ContactFormFieldType) =>
  CONTACT_FORM_CHOICE_FIELD_TYPES.includes(type);

export const ContactFormFieldsEditor = ({ control, disabled }: ContactFormFieldsEditorProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'presets.editor.advanced.contactForm.fields',
  });
  const { field } = useController({
    control,
    name: 'advanced.contact_form.fields',
  });
  const fields = field.value;
  const [draft, setDraft] = useState<ContactFormField | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draftErrors, setDraftErrors] = useState<FieldDraftErrors>({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const beginCreate = () => {
    setEditingIndex(null);
    setDraft(createEmptyField());
    setDraftErrors({});
  };

  const beginEdit = (index: number) => {
    setEditingIndex(index);
    setDraft({ ...fields[index], options: [...fields[index].options] });
    setDraftErrors({});
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditingIndex(null);
    setDraftErrors({});
  };

  const updateDraft = (changes: Partial<ContactFormField>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
  };

  const updateOption = (index: number, value: string) => {
    if (!draft) return;
    const options = [...draft.options];
    options[index] = value;
    updateDraft({ options });
    setDraftErrors((current) => ({ ...current, options: undefined }));
  };

  const saveDraft = () => {
    if (!draft) return;
    const alias = draft.alias.trim().toLowerCase();
    const label = draft.label.trim();
    const options = draft.options.map((option) => option.trim()).filter(Boolean);
    const normalizedOptions = options.map((option) => option.toLowerCase());
    const errors: FieldDraftErrors = {};

    if (!alias) errors.alias = t('errors.aliasRequired');
    else if (!CONTACT_FORM_ALIAS_PATTERN.test(alias)) errors.alias = t('errors.aliasFormat');
    else if (
      fields.some(
        (item, index) => index !== editingIndex && item.alias.trim().toLowerCase() === alias,
      )
    ) {
      errors.alias = t('errors.aliasUnique');
    }
    if (!label) errors.label = t('errors.labelRequired');
    if (isChoiceField(draft.type) && options.length < 2) {
      errors.options = t('errors.optionsMinimum');
    } else if (new Set(normalizedOptions).size !== normalizedOptions.length) {
      errors.options = t('errors.optionsUnique');
    }

    if (Object.keys(errors).length) {
      setDraftErrors(errors);
      return;
    }

    const savedField: ContactFormField = {
      ...draft,
      alias,
      label,
      placeholder: draft.placeholder?.trim() || null,
      options: isChoiceField(draft.type) ? options : [],
    };
    const nextFields = [...fields];
    if (editingIndex === null) nextFields.push(savedField);
    else nextFields[editingIndex] = savedField;
    field.onChange(nextFields);
    cancelEdit();
  };

  const removeField = (index: number) => {
    field.onChange(fields.filter((_, fieldIndex) => fieldIndex !== index));
    if (editingIndex === index) cancelEdit();
  };

  const reorderFields = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const fromIndex = fields.findIndex((item) => item.alias === active.id);
    const toIndex = fields.findIndex((item) => item.alias === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    field.onChange(arrayMove(fields, fromIndex, toIndex));
  };

  const fieldDraft = draft ? (
    <ContactFormFieldDraft
      draft={draft}
      errors={draftErrors}
      isCreating={editingIndex === null}
      disabled={disabled}
      onChange={updateDraft}
      onClearError={(errorField) =>
        setDraftErrors((current) => ({ ...current, [errorField]: undefined }))
      }
      onUpdateOption={updateOption}
      onCancel={cancelEdit}
      onSave={saveDraft}
    />
  ) : null;

  return (
    <div className={styles.contactFormFieldsSection}>
      <Flex vertical gap={4}>
        <Text variant="sm" fontWeight={600}>
          {t('title')}
        </Text>
        <Text variant="xs" className={styles.mutedText}>
          {t('description')}
        </Text>
      </Flex>

      {fields.length === 0 && !draft && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} />
      )}

      {fields.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderFields}>
          <SortableContext
            items={fields.map((item) => item.alias)}
            strategy={verticalListSortingStrategy}
          >
            <Flex vertical gap={8}>
              {fields.map((item, index) => {
                const actionsDisabled = Boolean(disabled || draft);

                return (
                  <SortableField key={item.alias} id={item.alias} disabled={actionsDisabled}>
                    {({ attributes, isDragging, listeners, setActivatorNodeRef }) => (
                      <>
                        <div
                          className={styles.additionalFieldCard}
                          style={{ opacity: isDragging ? 0.9 : 1 }}
                        >
                          <div className={styles.additionalFieldSummary}>
                            <Text variant="sm" fontWeight={600}>
                              {item.label}
                            </Text>
                            <Flex gap={6} wrap>
                              <Tag bordered={false}>{t(`types.${item.type}`)}</Tag>
                              <Tag bordered={false}>{item.alias}</Tag>
                              {item.required && (
                                <Tag bordered={false} color="blue">
                                  {t('requiredBadge')}
                                </Tag>
                              )}
                              {isChoiceField(item.type) && (
                                <Tag bordered={false}>
                                  {t('optionsCount', { count: item.options.length })}
                                </Tag>
                              )}
                            </Flex>
                          </div>
                          <Flex gap={4}>
                            <Button
                              type="text"
                              htmlType="button"
                              icon={<EditWithoutLine />}
                              aria-label={t('edit')}
                              disabled={actionsDisabled}
                              className={styles.iconButton}
                              onClick={() => beginEdit(index)}
                            />
                            <Popconfirm
                              title={t('deleteConfirmTitle')}
                              description={t('deleteConfirmDescription')}
                              okText={t('delete')}
                              cancelText={t('cancel')}
                              okButtonProps={{ danger: true }}
                              disabled={actionsDisabled}
                              onConfirm={() => removeField(index)}
                            >
                              <Button
                                danger
                                type="text"
                                htmlType="button"
                                icon={<Delete />}
                                aria-label={t('delete')}
                                disabled={actionsDisabled}
                                className={styles.iconButton}
                              />
                            </Popconfirm>
                            <button
                              ref={setActivatorNodeRef}
                              type="button"
                              className={styles.fieldDragHandle}
                              disabled={actionsDisabled}
                              {...attributes}
                              {...listeners}
                              aria-label={t('drag')}
                            >
                              <DragMenu />
                            </button>
                          </Flex>
                        </div>
                        {editingIndex === index && fieldDraft}
                      </>
                    )}
                  </SortableField>
                );
              })}
            </Flex>
          </SortableContext>
        </DndContext>
      )}

      {editingIndex === null && fieldDraft}

      {!draft && (
        <Button
          type="dashed"
          htmlType="button"
          block
          icon={<PlusBlueWithoutBackground />}
          disabled={disabled || fields.length >= MAX_CONTACT_FORM_FIELDS}
          className={styles.addContactFormFieldButton}
          onClick={beginCreate}
        >
          {t('addField')}
        </Button>
      )}
    </div>
  );
};
